import React, { useState, useEffect, useCallback } from 'react';
import { 
  MessageSquare, Settings, CreditCard, Send, 
  Check, AlertCircle, Loader2, Package, 
  ChevronLeft, ChevronRight, RefreshCw, Phone, PoundSterling,
  Smartphone, Info
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import ClickSendSenderPanel from '../../components/ClickSendSenderPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const SMSSettings = ({ onBack }) => {
  const [settings, setSettings] = useState({
    sms_enabled: false,
    sms_sender_id: '',
    sms_credits: 0,
    sms_credits_used: 0
  });
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [customCredits, setCustomCredits] = useState('');
  const [purchasing, setPurchasing] = useState(false);

  // Check URL params for purchase status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchaseStatus = params.get('purchase');
    const sessionId = params.get('session_id');
    
    if (purchaseStatus === 'success' && sessionId) {
      // Add credits after successful payment
      addCreditsAfterPayment(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (purchaseStatus === 'cancelled') {
      toast.error('Purchase cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const addCreditsAfterPayment = async (sessionId) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API}/api/sms/credits/add?session_id=${sessionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchSettings();
      }
    } catch (error) {
      console.error('Error adding credits:', error);
    }
  };

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API}/api/sms/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching SMS settings:', error);
      toast.error('Failed to load SMS settings');
    }
  }, []);

  const fetchPricing = useCallback(async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API}/api/sms/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchPricing();
  }, [fetchSettings, fetchPricing]);

  const handleSaveSettings = async () => {
    console.log('Save button clicked, current settings:', settings);
    try {
      setSaving(true);
      const token = localStorage.getItem('streakfree_token');
      
      if (!token) {
        toast.error('Not logged in. Please refresh and login again.');
        return;
      }
      
      console.log('Sending request to:', `${API}/api/sms/settings`);
      console.log('Payload:', { sms_enabled: settings.sms_enabled, sms_sender_id: settings.sms_sender_id });
      
      const response = await fetch(`${API}/api/sms/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sms_enabled: settings.sms_enabled,
          sms_sender_id: settings.sms_sender_id
        })
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Save response:', data);
        toast.success('SMS settings saved successfully!');
        // Re-fetch to confirm save
        await fetchSettings();
      } else {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Save failed:', error);
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const calculatePrice = (credits) => {
    if (!pricing) return { subtotal: 0, vat: 0, total: 0, pricePerCredit: 0 };
    
    let pricePerCredit;
    if (credits >= 5000) {
      pricePerCredit = pricing.pricing_tiers.tier3.price_per_credit;
    } else if (credits >= 1000) {
      pricePerCredit = pricing.pricing_tiers.tier2.price_per_credit;
    } else {
      pricePerCredit = pricing.pricing_tiers.tier1.price_per_credit;
    }
    
    const subtotal = credits * pricePerCredit;
    const vat = subtotal * pricing.vat_rate;
    const total = subtotal + vat;
    
    return {
      subtotal: subtotal.toFixed(2),
      vat: vat.toFixed(2),
      total: total.toFixed(2),
      pricePerCredit: pricePerCredit.toFixed(4)
    };
  };

  const handlePurchase = async () => {
    const credits = selectedPack?.credits || parseInt(customCredits);
    
    if (!credits || credits < 50) {
      toast.error('Minimum purchase is 50 credits');
      return;
    }
    
    try {
      setPurchasing(true);
      const token = localStorage.getItem('streakfree_token');
      
      const response = await fetch(`${API}/api/sms/credits/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ credits })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start purchase');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to process purchase');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="sms-settings-page">
      {/* Header with Back Button */}
      <div className="bg-white border-b border-zinc-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft size={22} className="text-zinc-600" />
          </button>
        )}
        <div className="flex items-center gap-3 flex-1">
          <div className="p-2.5 bg-green-100 rounded-xl">
            <MessageSquare className="text-green-600" size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-zinc-900">SMS Settings</h1>
            <p className="text-xs text-zinc-500">Configure SMS messaging for your customers</p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 max-w-2xl mx-auto w-full">
        {/* Credits Balance Card */}
        <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/70">SMS Credits Balance</p>
                <p className="text-4xl font-bold" data-testid="credits-balance">
                  {settings.sms_credits.toLocaleString()}
                </p>
                <p className="text-sm text-white/60 mt-1">
                  {settings.sms_credits_used.toLocaleString()} credits used
                </p>
            </div>
            <Button
              onClick={() => setShowPurchaseModal(true)}
              className="bg-white text-green-700 hover:bg-green-50"
              data-testid="buy-credits-btn"
            >
              <Package size={18} className="mr-2" />
              Buy Credits
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SMS Enable Toggle */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings size={20} />
            SMS Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Enable SMS Messaging</p>
              <p className="text-sm text-gray-500">Allow sending SMS to customers</p>
            </div>
            <Switch
              checked={settings.sms_enabled}
              onCheckedChange={(checked) => setSettings(s => ({ ...s, sms_enabled: checked }))}
              data-testid="sms-enabled-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sender ID — ClickSend OTP-backed flow.
          Replaces the legacy alphanumeric-only input; this version auto-
          detects whether the value is a brand or a UK mobile number and
          drives the verification flow inline (no separate save button
          required when entering a phone number). */}
      <ClickSendSenderPanel />

      {/* Pricing Info */}
      {pricing && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <PoundSterling size={20} />
              SMS Credit Pricing
            </CardTitle>
            <CardDescription>Volume discounts on bulk purchases</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-600">1-999 credits</span>
                <span className="font-medium">£{pricing.pricing_tiers.tier1.price_per_credit.toFixed(3)} each</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-600">1,000-4,999 credits</span>
                <span className="font-medium text-green-600">£{pricing.pricing_tiers.tier2.price_per_credit.toFixed(3)} each</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">5,000+ credits</span>
                <span className="font-medium text-green-600">£{pricing.pricing_tiers.tier3.price_per_credit.toFixed(3)} each</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              1 credit = 1 SMS segment (160 GSM chars or 70 Unicode chars)
            </p>
          </CardContent>
        </Card>
      )}

        {/* Save Button */}
        <Button
          onClick={handleSaveSettings}
          disabled={saving}
          className="w-full bg-green-600 hover:bg-green-700"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 size={18} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check size={18} className="mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Purchase Credits Modal */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="max-w-md" data-testid="purchase-modal">
          <DialogHeader>
            <DialogTitle>Purchase SMS Credits</DialogTitle>
            <DialogDescription>
              Choose a credit pack or enter a custom amount
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Credit Packs */}
            <div className="grid grid-cols-2 gap-3">
              {pricing?.credit_packs.map((pack) => (
                <button
                  key={pack.credits}
                  onClick={() => {
                    setSelectedPack(pack);
                    setCustomCredits('');
                  }}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    selectedPack?.credits === pack.credits
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                  data-testid={`pack-${pack.credits}`}
                >
                  <p className="font-bold text-lg">{pack.credits.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">credits</p>
                  <p className="text-green-600 font-semibold mt-1">£{pack.price.toFixed(2)}</p>
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="pt-3 border-t">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or enter custom amount:
              </label>
              <Input
                type="number"
                min="50"
                value={customCredits}
                onChange={(e) => {
                  setCustomCredits(e.target.value);
                  setSelectedPack(null);
                }}
                placeholder="Min 50 credits"
                data-testid="custom-credits-input"
              />
              {customCredits && parseInt(customCredits) >= 50 && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>£{calculatePrice(parseInt(customCredits)).subtotal}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>VAT (20%):</span>
                    <span>£{calculatePrice(parseInt(customCredits)).vat}</span>
                  </div>
                  <div className="flex justify-between font-bold mt-1 pt-1 border-t">
                    <span>Total:</span>
                    <span>£{calculatePrice(parseInt(customCredits)).total}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={purchasing || (!selectedPack && (!customCredits || parseInt(customCredits) < 50))}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-purchase-btn"
            >
              {purchasing ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={18} className="mr-2" />
                  Purchase
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMSSettings;
